import _get from 'lodash/get';
import _some from 'lodash/some';
import { FeFilterPresetEntitiesInputObject } from 'graphql-common';
import MonitoringFilterFieldNames from '@lib/enums/fieldNames/monitoringFilterFieldNames';
import { Values, Option } from '@lib/interfaces/form';
import {
  MonitoringEntity,
  FilterPresetType,
  MonitoringType,
} from 'routes/Dashboard/Monitoring/enums';
import getFeFilterPresetKind from './getFeFilterPresetKind';
import getFilterFieldNameByMonitoringEntity from './getFilterFieldNameByMonitoringEntity';
import getLegendFilterName from './getLegendFilterName';
import getStatusKind from './getStatusKind';

interface CreateFilterPresetVariablesParams {
  values: Values;
  filterParams: Values;
  monitoringEntity: MonitoringEntity;
}

export default function createFilterPresetVariables({
  values,
  filterParams,
  monitoringEntity,
}: CreateFilterPresetVariablesParams) {
  const filterPresetConfig = _get(
    values,
    MonitoringFilterFieldNames.FilterPresetConfig,
    [],
  );
  const filterPreset =
    Array.isArray(filterPresetConfig) && filterPresetConfig.length > 0
      ? filterPresetConfig
      : [];
  const filtersPreset = _some(filterPreset, {
    value: FilterPresetType.Filters,
  });
  const periodPreset = _some(filterPreset, {
    value: FilterPresetType.Period,
  });
  const rangesPreset = _some(filterPreset, {
    value: FilterPresetType.Ranges,
  });
  const mapLegendsPreset = _some(filterPreset, {
    value: FilterPresetType.MapLegends,
  });

  const fieldNamePeriod = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.Period,
  );
  const fieldNameStatus = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.Status,
  );
  const fieldNameFormCategory = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.FormCategory,
  );
  const fieldNameFormTemplateName = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.FormTemplateName,
  );

  let presetData = { [fieldNameStatus]: _get(filterParams, fieldNameStatus) };
  let entities: FeFilterPresetEntitiesInputObject = {};

  if (filtersPreset) {
    const formCategories = _get(
      filterParams,
      fieldNameFormCategory,
    ) as Option[];
    const formTemplates = _get(
      filterParams,
      fieldNameFormTemplateName,
    ) as Option[];
    entities = {
      ...entities,
      formCategories: formCategories?.length
        ? formCategories.map(({ value }) => ({
            id: value as string,
          }))
        : undefined,
      formTemplates: formTemplates?.length
        ? formTemplates.map(({ value }) => ({
            id: value as string,
          }))
        : undefined,
    };
    presetData = {
      ...presetData,
      [fieldNameStatus]: _get(filterParams, fieldNameStatus),
      // Marker to indicate that "Filters" section was included into preset
      // Consumers should respect it when applying (e.g., overwrite status/categories/templates only if true)
      filtersPreset: true,
    };
  }
  if (periodPreset) {
    presetData = {
      ...presetData,
      [fieldNamePeriod]: filterParams[fieldNamePeriod],
    };
  }
  if (rangesPreset && monitoringEntity === MonitoringEntity.Site) {
    const sites = _get(
      filterParams,
      MonitoringFilterFieldNames.Sites,
    ) as Option[];
    if (sites?.length) {
      entities = {
        ...entities,
        sites: sites.map(({ value }) => ({ id: value as string })),
      };
    }
  }
  if (rangesPreset && monitoringEntity === MonitoringEntity.Sector) {
    const sectors = _get(
      filterParams,
      MonitoringFilterFieldNames.Sectors,
    ) as Option[];
    if (sectors?.length)
      entities = {
        ...entities,
        sectors: sectors.map(({ value }) => ({ id: value as string })),
      };
  }

  if (mapLegendsPreset) {
    const performanceKey = getLegendFilterName(
      monitoringEntity,
      MonitoringType.Performance,
    );
    const performanceVal = _get(filterParams, performanceKey);

    const statusKind = getStatusKind({ filterParams });
    const anomalyGenericKey = getLegendFilterName(
      monitoringEntity,
      MonitoringType.Anomaly,
    );
    const anomalyStatusKey = getLegendFilterName(
      monitoringEntity,
      MonitoringType.Anomaly,
      statusKind,
    );
    const anomalyVal =
      _get(filterParams, anomalyStatusKey) ??
      _get(filterParams, anomalyGenericKey);

    presetData = {
      ...presetData,
      ...(performanceVal !== undefined
        ? { [performanceKey]: performanceVal }
        : {}),
      ...(anomalyVal !== undefined ? { [anomalyGenericKey]: anomalyVal } : {}),
    };
  }
  const kind = getFeFilterPresetKind(monitoringEntity);

  if (!kind) {
    throw new Error('No FeFilterPresetsKind');
  }

  return {
    attributes: {
      name: _get(values, MonitoringFilterFieldNames.Name, '') as string,
      kind,
      data: presetData,
      entities,
    },
  };
}
